/**
 * 423 (#1546, mock-up frame 5): the odometer readings the customer typed at pickup (with the fuel photo) and at return,
 * the distance between them, and Cartrack's reading for the same moments beside them when the platform kept one. The
 * customer's readings are the record (PO 2026-10-27); Cartrack's never fills a gap. Extra-kilometre charges (417) keep
 * reading Cartrack's measured distance, not these.
 */
export interface OdometerSet {
  odometer_km?: number;
  created_at?: string;
  cartrack_odometer?: { km: number | null; fix_at: string | null; read_at: string };
}

const km = (n: number) => `${Math.round(n).toLocaleString('en-MY')} km`;
const when = (iso: string) =>
  new Date(iso)
    .toLocaleString('en-GB', {
      timeZone: 'Asia/Kuala_Lumpur',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    .replace(' at', ',');

function distance(from?: number | null, to?: number | null): string {
  if (typeof from !== 'number' || typeof to !== 'number' || to < from) return '—';
  return km(to - from);
}

export function OdometerCard({ pickup, ret }: { pickup?: OdometerSet; ret?: OdometerSet }) {
  if (!pickup && !ret) return null;
  const custP = pickup?.odometer_km;
  const custR = ret?.odometer_km;
  const ctP = pickup?.cartrack_odometer?.km ?? null;
  const ctR = ret?.cartrack_odometer?.km ?? null;
  const cartrack = ctP !== null || ctR !== null;
  const lower = typeof custP === 'number' && typeof custR === 'number' && custR < custP;
  const cols = cartrack
    ? 'grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]'
    : 'grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]';
  const value = (id: string, v: number | null | undefined) => (
    <span
      data-testid={id}
      className={
        typeof v === 'number'
          ? 'font-semibold text-ink tabular-nums'
          : 'italic text-steel dark:text-slate'
      }
    >
      {typeof v === 'number' ? km(v) : 'not sent'}
    </span>
  );
  const row = (label: string, moment: 'pickup' | 'return', set?: OdometerSet) => (
    <div className={`grid ${cols} items-center gap-2 border-t border-hairline-soft py-2 text-sm`}>
      <span className="text-steel dark:text-slate">
        {label}
        {set?.created_at ? (
          <span data-testid={`odo-when-${moment}`} className="block text-xs">
            {when(set.created_at)}
          </span>
        ) : null}
      </span>
      {value(`odo-customer-${moment}`, set?.odometer_km)}
      {cartrack ? (
        <span data-testid={`odo-cartrack-${moment}`} className="text-ink tabular-nums">
          {typeof set?.cartrack_odometer?.km === 'number' ? km(set.cartrack_odometer.km) : '—'}
        </span>
      ) : null}
    </div>
  );

  return (
    <div
      data-testid="odometer-card"
      className="mb-4 rounded-lg border border-hairline p-3 dark:border-hairline-strong"
    >
      <h3 className="mb-2 text-sm font-semibold text-ink">Odometer</h3>
      <div
        className={`grid ${cols} gap-2 pb-1 text-[11px] font-semibold tracking-wider text-steel uppercase dark:text-slate`}
      >
        <span />
        <span>Customer typed</span>
        {cartrack ? <span>Cartrack</span> : null}
      </div>
      {row('Pickup', 'pickup', pickup)}
      {row('Return', 'return', ret)}
      <div className={`grid ${cols} items-center gap-2 border-t border-hairline-soft py-2 text-sm`}>
        <span className="text-steel dark:text-slate">Distance</span>
        <span data-testid="odo-customer-distance" className="font-semibold text-ink tabular-nums">
          {distance(custP, custR)}
        </span>
        {cartrack ? (
          <span data-testid="odo-cartrack-distance" className="text-ink tabular-nums">
            {distance(ctP, ctR)}
          </span>
        ) : null}
      </div>
      {lower ? (
        <p
          data-testid="odo-flag"
          className="mt-2 rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-charcoal"
        >
          Return reading is lower than pickup. Check the dashboard photos; the customer may have
          mistyped.
        </p>
      ) : null}
    </div>
  );
}
